import { ESdChangeReason } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { IPureDesignComponentState, PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaDropdownMenu, {
  KuikaDropdownMenuItemType
} from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaLoader from "../../../../ui-kit/kuika-loader/kuika-loader";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import { EditMode } from "./styling-selector";
import "./styling-selector.scss";

export interface IStylingTagProps {
  label: any;
  value: any;
  closable: boolean;
  onClose?: () => void;
  isDefaultStyling: (stylingId: string) => boolean;
  isCurrentComponentDefaultStyling: (stylingId: string) => boolean;
  stylingVersionMenu: (label: string, onClose?: () => void) => KuikaDropdownMenuItemType[];
  mode?: EditMode;
  cancel?: () => void;
  save?: (newValue?: string) => void;
  isStylingMenuVisible?: boolean;
  setIsStylingMenuVisible?: (visible: boolean) => void;
  isAnyEditing?: boolean;
  saving?: boolean;
  hasPendingChanges?: boolean;
}

interface IStylingTagState {
  inputValue: string;
}

class StylingTag extends PureSdComponent<
  IStylingTagProps & IWithSDProps & IWithContextProp,
  IStylingTagState & IPureDesignComponentState
> {
  constructor(props: IStylingTagProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      inputValue: this.props.label
    };
  }

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ inputValue: e.target.value });
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      this.handleCancel(e);
      this.props.setIsStylingMenuVisible?.(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      this.handleSave(e);
      this.props.setIsStylingMenuVisible?.(false);
    }
  };

  handleSave = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (this.props.saving) return;
    this.props.save?.(this.state.inputValue);
    this.props.setIsStylingMenuVisible?.(false);
  };

  handleCancel = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    this.setState({ inputValue: this.props.label });
    this.props.cancel?.();
    this.props.setIsStylingMenuVisible?.(false);
  };

  render() {
    const {
      label,
      value,
      closable,
      onClose,
      isDefaultStyling,
      isCurrentComponentDefaultStyling,
      stylingVersionMenu,
      mode,
      isStylingMenuVisible,
      setIsStylingMenuVisible,
      isAnyEditing
    } = this.props;

    if (isCurrentComponentDefaultStyling(value)) {
      return null;
    }

    const isDefault = isDefaultStyling(value);
    // While renaming a tag (edit mode) a click on the chip must NOT open the
    // styling version menu — doing so applies the `__open` class which jumps the
    // background to a darker blue (visible on default tags) and pops a menu that
    // makes no sense mid-rename. Keep the menu controlled-closed and swallow the
    // open request until edit mode ends.
    const isEditing: boolean = mode === EditMode.edit;

    return (
      <KuikaDropdownMenu
        options={stylingVersionMenu(label, onClose)}
        trigger="click"
        open={isEditing ? false : isStylingMenuVisible}
        setOpen={(visible: boolean) => {
          if (isEditing) {
            return;
          }
          setIsStylingMenuVisible?.(visible);
        }}
        getPopupContainer={() => document.body}
        overlayClassName="kuika-styling-selector__dropdown-menu-overlay"
      >
        <div
          data-testid="stylingTag"
          // The chip is the overflow host: a long name slides whenever the chip is hovered or focused,
          // not only when the pointer happens to be over the text itself.
          className={classNames("kuika-styling-selector__class-name", "kuika-overflow-text-host", {
            "kuika-styling-selector__class-name__default": isDefault,
            "kuika-styling-selector__class-name__open": isStylingMenuVisible,
            editing: mode === EditMode.edit,
            "dimmed-mode": isAnyEditing && mode !== EditMode.edit
          })}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          tabIndex={0}
        >
          {mode === EditMode.edit ? (
            <div className="kuika-flex--gap kuika-align-center kuika-styling-selector__edit-container">
              <div className="kuika-styling-selector__input-wrapper">
                <KuikaSmallMediumText className="kuika-styling-selector__hidden-text">
                  {this.state.inputValue}
                </KuikaSmallMediumText>
                <input
                  type="text"
                  value={this.state.inputValue}
                  onChange={this.handleInputChange}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={this.handleKeyDown}
                  className="kuika-styling-selector__input"
                  data-testid="stylingTagNameInput"
                  autoFocus
                />
              </div>
              <div className="kuika-styling-selector__actions">
                {this.props.saving ? (
                  <KuikaLoader size="sm" label="Saving" className="kuika-styling-selector__save-spinner" />
                ) : (
                  <>
                    <KuikaSvg
                      name="check"
                      width={12}
                      height={12}
                      onClick={this.handleSave}
                      data-testid="stylingTagSaveButton"
                    />
                    <KuikaSvg
                      name="x"
                      width={12}
                      height={12}
                      onClick={this.handleCancel}
                      data-testid="stylingTagCancelButton"
                    />
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* No `title` here: the Style list's rows are addressed by theirs (StyleTagPage), and a
                  chip carrying the same name would make that lookup match twice. */}
              <KuikaOverflowText className="kuika-styling-selector__label">
                <KuikaSmallMediumText>{label}</KuikaSmallMediumText>
              </KuikaOverflowText>
              <div
                className={classNames("kuika-styling-selector__class-name__chevron-down-wrapper", {
                  "kuika-styling-selector__class-name__chevron-down-wrapper--visible": isStylingMenuVisible
                })}
              >
                <KuikaSvg
                  className={classNames("kuika-styling-selector__class-name__chevron-down")}
                  name="dots"
                  width={12}
                  height={12}
                />
              </div>
            </>
          )}
        </div>
      </KuikaDropdownMenu>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.ChangePropertyValue ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeStyleValue ||
      changeReason === ESdChangeReason.ChangeStyleValueExternal ||
      changeReason === ESdChangeReason.ChangeProjectStyle
    );
  };
}

const stylingTagComponent = withContext<IStylingTagProps>(withSD<IStylingTagProps>(StylingTag));
export { stylingTagComponent as StylingTag };
